<template>
  <Modal position="end" ref="modalRef" @after-close="clearForm">
    <template #header>Add new lead</template>

    <template #body>
      <FormGroup>
        <View gap="sm" direction="column">
          <Text variant="h2">Add new lead’s info</Text>
          <Text variant="body-large" color="tertiary">
            If you already have someone interested in your property, add them here and Tallo will handle the
            communication and scheduling
          </Text>
        </View>

        <FormField label="Full name" :error="form.fieldErrors('name')">
          <Input v-model="form.fields.name" placeholder="Type full name" />
        </FormField>

        <FormField label="Phone number" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>

        <FormField label="Email" :error="form.fieldErrors('email')">
          <Input v-model="form.fields.email" type="email" placeholder="Type email" />
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close">Cancel</Button>
      <Button @click="save" :loading="form.submitting">Add</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { email, helpers, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref } from 'vue';

import {
  phoneNumberValidator,
  Button,
  FormField,
  FormGroup,
  Input,
  InputPhone,
  Modal,
  Text,
  View,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { getPropertyStatisticsQueryKey } from '@tallo/investor/property';

import leadsService from '../services/leads.service';

const props = defineProps<{ propertyId: string }>();
const alertsStore = useAlertsStore();
const queryCache = useQueryCache();
const modalRef = ref<InstanceType<typeof Modal>>();
const form = useForm(
  {
    name: '',
    phone: undefined,
    email: '',
  },
  {
    name: {
      required: helpers.withMessage('Full name is required', required),
    },
    phone: {
      phoneNumberValidator,
    },
    email: {
      email: helpers.withMessage('Email is not valid', email),
      required: helpers.withMessage('Email is required', required),
    },
  },
);

function save() {
  // filter empty fields from form and then submit
  Object.keys(form.fields).forEach((key) => {
    if (!form.fields[key as keyof typeof form.fields]) {
      delete form.fields[key as keyof typeof form.fields];
    }
  });

  form.submit(async () => {
    return leadsService
      .add({ propertyId: props.propertyId, ...form.fields })
      .then(() => {
        alertsStore.success(`Tallo will contact <b>${form.fields.name}</b> shortly`);
        queryCache.invalidateQueries({ key: getPropertyStatisticsQueryKey(props.propertyId) });
        modalRef.value?.close();
      })
      .catch((err) => {
        if (err instanceof AxiosError && !err.response?.data?.validationErrors) {
          if (err.response?.data?.message?.message) {
            alertsStore.error(err.response.data.message.message);
          } else {
            alertsStore.somethingWentWrong();
          }
        }

        throw err;
      });
  });
}

function clearForm() {
  Object.assign(form.fields, {
    name: '',
    phone: '',
    email: '',
  });
}

defineExpose({ modalRef });
</script>
